// ManagementPage.js
import React, { useEffect, useState, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { BASEAPIURL, token } from "../../env";

let searchTimeout;

const ManagementPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMemberID, setEditingMemberID] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [formData, setFormData] = useState({
    ManagementComID: "",
    MemberId: "",
    name: "",
    BusinessTitle: "",
    Designation: "",
    Address: "",
    JoiningDate: "",
    Timestamp: "",
    Status: "Active",
  });

  const photoRef = useRef(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = () => {
    setLoading(true);
    fetch(`${BASEAPIURL}/GetManagementCommunity`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tokenData: token }),
    })
      .then((res) => res.json())
      .then((result) => {
        setMembers(result?.data || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
        setLoading(false);
      });
  };

  const searchMemberByName = (name) => {
    if (!name.trim()) return;
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      fetch(`${BASEAPIURL}/MemberSearchedList?Search=${name}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tokenData: token }),
      })
        .then((res) => res.json())
        .then((data) => {
          const results = data?.data || [];
          setSearchResults(results);

          const matched = results.find(
            (m) => m.Name.toLowerCase() === name.toLowerCase()
          );
          if (matched) {
            setFormData((prev) => ({ ...prev, MemberId: matched.MemID }));
          }
        })
        .catch((err) => console.error("Search error:", err));
    }, 300);
  };

  const openAddModal = () => {
    setFormData({
      ManagementComID: "",
      MemberId: "",
      name: "",
      BusinessTitle: "",
      Designation: "",
      Address: "",
      JoiningDate: "",
      Timestamp: "",
      Status: "Active",
    });
    setPreviewUrl(null);
    setEditingMemberID(null);
    if (photoRef.current) photoRef.current.value = "";
    setShowModal(true);
  };

  const handleEdit = (member) => {
    setFormData({
      ManagementComID: member.ManagementComID || "",
      MemberId:
        member.MemberID ||
        member.MemberId ||
        member.MemID ||
        formData.memberId ||
        "",
      name: member.Name || "",
      BusinessTitle: member.BusinessTitle || "",
      Designation: member.Designation || "",
      Address: member.Address || member.MemAddress || "",
      JoiningDate: member.JoiningDate?.split("T")[0] || "",
      Timestamp: member.Timestamp ? member.Timestamp.slice(0, 16) : "",
      Status: member.Status || "Active",
    });

    setPreviewUrl(member.Photo || null);
    setEditingMemberID(member.ManagementComID);
    if (photoRef.current) photoRef.current.value = "";
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "name") searchMemberByName(value);
  };

  const uploadPhotoIfNeeded = async () => {
    const file = photoRef.current?.files[0];
    if (!file) return previewUrl;

    const form = new FormData();
    form.append("token", token);
    form.append("Photo", file);
    form.append("name", formData.name);
    if (formData.MemberId) form.append("MemberID", formData.MemberId);

    const res = await fetch(
      `${BASEAPIURL}/UploadManagementCommunity`,
      {
        method: "POST",
        body: form,
      }
    );

    const result = await res.text();
    return result.includes("http") ? result : previewUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.MemberId || isNaN(formData.MemberId)) {
      alert("Please select a valid member from the suggestion list.");
      return;
    }

    try {
      const photoUrl = await uploadPhotoIfNeeded();

      if (editingMemberID) {
        await updateMember(photoUrl);
      } else {
        await insertMember(photoUrl);
      }

      fetchMembers();
      resetForm();
      alert("Member saved successfully.");
    } catch (err) {
      console.error("Submit error:", err);
      alert("Error saving member.");
    }
  };

  const insertMember = async (photoUrl) => {
    const payload = {
      tokenData: token,
      Management: {
        MemberID: formData.MemberId,
        Name: formData.name,
        BusinessTitle: formData?.BusinessTitle,
        Designation: formData.Designation,
        Address: formData.Address,
        JoiningDate: formData.JoiningDate || "2000-01-01",
        Timestamp: formData.Timestamp || new Date().toISOString(),
        Status: formData.Status,
        Photo: photoUrl || "",
      },
    };

    await fetch(`${BASEAPIURL}/InsertManagementCommunity`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  };

  const updateMember = async (photoUrl) => {
    const payload = {
      tokenData: token,
      Management: {
        ManagementComID: formData.ManagementComID,
        MemberID: formData.MemberId,
        Name: formData.name,
        BusinessTitle: formData.BusinessTitle,
        Designation: formData.Designation,
        Address: formData.Address,
        JoiningDate: formData.JoiningDate || "2000-01-01",
        Timestamp: formData.Timestamp || new Date().toISOString(),
        Status: formData.Status,
        Photo: photoUrl || "",
      },
    };

    await fetch(
      `${BASEAPIURL}/UpdateManagementCommunity?ManagementComID=${formData.ManagementComID}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
  };

  const resetForm = () => {
    setFormData({
      ManagementComID: "",
      MemberId: "",
      name: "",
      BusinessTitle: "",
      Designation: "",
      Address: "",
      JoiningDate: "",
      Timestamp: "",
      Status: "Active",
    });
    setShowModal(false);
    setEditingMemberID(null);
    setPreviewUrl(null);
    if (photoRef.current) photoRef.current.value = "";
    setSearchResults([]);
  };

  return (
    <div className="container mb-5" style={{ marginTop: "-30px" }}>
      <h2 className="text-center mb-4">Management Team</h2>
      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-success" onClick={openAddModal}>
          + Add
        </button>
      </div>

      {/* Modal Form */}
      {showModal && (
        <div
          className="modal show fade d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="modal-dialog modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5>{editingMemberID ? "Edit Member" : "Add Member"}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={resetForm}
                  ></button>
                </div>
                <div
                  className="modal-body overflow-auto"
                  style={{ maxHeight: "70vh" }}
                >
                  <div className="row g-3">
                    <div className="col-md-6 position-relative">
                      <input
                        type="text"
                        name="name"
                        required
                        className="form-control"
                        placeholder="Name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                      {searchResults.length > 0 && (
                        <ul
                          className="list-group position-absolute w-100 overflow-auto"
                          style={{ maxHeight: "200px", zIndex: 1000 }}
                        >
                          {searchResults.map((m) => (
                            <li
                              key={m.MemID}
                              className="list-group-item list-group-item-action"
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  name: m.MemName,
                                  MemberId: m.MemID,
                                }));
                                setSearchResults([]);
                              }}
                            >
                              {m.MemName}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="col-md-6">
                      <input
                        type="text"
                        name="Designation"
                        required
                        className="form-control"
                        placeholder="Designation"
                        value={formData.Designation}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="text"
                        name="BusinessTitle"
                        required
                        className="form-control"
                        placeholder="BusinessTitle"
                        value={formData.BusinessTitle}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="text"
                        name="Address"
                        className="form-control"
                        placeholder="Address"
                        value={formData.Address}
                        onChange={handleChange}
                      />
                    </div>
                    {/* <div className="col-md-6">
                      <input
                        type="date"
                        name="JoiningDate"
                        className="form-control"
                        value={formData.JoiningDate}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="datetime-local"
                        name="Timestamp"
                        className="form-control"
                        value={formData.Timestamp}
                        onChange={handleChange}
                      />
                    </div> */}
                    <div className="col-md-6">
                      <label htmlFor="Status">Active Status</label>
                      <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          className={`form-check-input ${
                            formData.Status ? "bg-success" : "bg-secondary"
                          }`}
                          id="Status"
                          checked={formData.Status}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              Status: e.target.checked,
                            })
                          }
                          style={{ width: "50px" }}
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      {previewUrl && (
                        <div className="text-center mb-2">
                          <img
                            src={previewUrl}
                            alt="Preview"
                            style={{
                              width: 100,
                              height: 100,
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        ref={photoRef}
                        className="form-control"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) setPreviewUrl(URL.createObjectURL(file));
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!formData.MemberId}
                  >
                    {editingMemberID ? "Update" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Members Display */}
      {loading ? (
        <p>Loading...</p>
      ) : members.length === 0 ? (
        <p>No members found.</p>
      ) : (
        <div className="row">
          {members.map((member) => (
            <div className="col-md-4 mb-2" key={member.ManagementComID}>
              <div className="card p-1 text-center h-60">
                <span
                  className={`badge ${
                    member.Status === "Active" ? "bg-success" : "bg-danger"
                  }`}
                  style={{ position: "absolute", top: 5, left: 10 }}
                >
                  {member.Status}
                </span>
                <img
                  src={member.Photo || "https://via.placeholder.com/100"}
                  className="card-img-top rounded-circle mx-auto mt-1"
                  alt="member"
                  style={{ width: 100, height: 100, objectFit: "cover" }}
                />
                <div className="card-body">
                  <h5>{member.Name}</h5>
                  <p>{member.BusinessTitle}</p>
                  <p>{member.Designation}</p>
                  <p>{member.MemAddress}</p>
                </div>
                <div className="card-footer">
                  {/* <small>
                    Joined:{" "}
                    {member.JoiningDate
                      ? new Date(member.JoiningDate).toLocaleDateString()
                      : ""}
                  </small> */}
                  {/* <br /> */}
                  <button
                    className="btn btn-sm btn-outline-primary mt-1"
                    onClick={() => handleEdit(member)}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManagementPage;
